import {useAuth} from "../../../../context";
import {useEffect, useState} from "react";
import {config} from "../../../../utils2";
import {fetchWrapper} from "../../../../_services/fetch";
import {useSearchParams} from "react-router-dom";
import * as yup from "yup";

const api = config.get_remote_api() + "/api/countries"

const endpointOrderOptions = (userId) => `${api}/order`
const endpointDetails = (userId, countryId) => `${api}/${countryId}`
const endpointCount = (userId, search) => `${api}/count${fetchWrapper.getSearchParams(search)}`
const endpointList = (userId, search) => `${api}/list${fetchWrapper.getSearchParams(search)}`

const endpointAdd = (userId) => `${api}/add`
const endpointEdit = (userId, targetId) => `${api}/${targetId}`
const endpointDelete = (userId, targetId) => `${api}/${targetId}`

const msg = "Failed with no error messages from the server."
const handleSuccess = resp => resp.success ? Promise.resolve(resp?.data) : Promise.reject({message: resp.message || msg})
const handleError = err => err?.name !== "AbortError" ? err?.error || err?.message || msg : null

export function useCountry(userId, coid = null) {

    const defaultValues = {
        id: null,
        name: "",
        code: "",
        created_at: null,
        updated_at: null,
        media_count: 0,
        product_count: 0,
        device_count: 0,
        vat: 0
    }

    const validationSchema = yup.object().shape({
        name: yup.string().required("Name cannot be empty").max(64),
        code: yup.string().required("Code cannot be empty").max(4),
        vat: yup.number().typeError("VAT must be a valid number").required("VAT is required")
    })

    const {token} = useAuth()
    const {value, setValue, loadingValue, setLoadingValue, errorValue, setErrorValue} = useCountryDetails(coid, defaultValues)


    function handleSuccessInt(resp) {
        if (resp.success) {
            setValue(resp?.data ? resp.data : defaultValues);
            return Promise.resolve(resp.message);
        } else {
            return Promise.reject({message: resp?.message || "Fetch failed with no error messages from the server."});
        }
    }

    const handleErrorInt = err => {
        setErrorValue(handleError(err))
        return Promise.reject(handleError(err))
    }


    function addItem(formValue) {
        return fetchWrapper.post(endpointAdd(userId), formValue, token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function editItem(formValue, targetId = coid) {
        return fetchWrapper.put(endpointEdit(userId, targetId), formValue, token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function deleteItem(targetId = coid) {
        return fetchWrapper.delete(endpointDelete(userId, targetId), token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    return {
        value,
        validationSchema,
        addItem,
        editItem,
        deleteItem,
        loadingValue,
        errorValue,
        clearErrorValue: () => setErrorValue(null)
    }
}


export function useCountryDetails(countryId, defaultValue) {

    const {loggedIn, userId, token} = useAuth()
    const [loadingValue, setLoadingValue] = useState(false);
    const [errorValue, setErrorValue] = useState(null);
    const [value, setValue] = useState(defaultValue);

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn && countryId && countryId !== "add") {
            setErrorValue(null)
            setLoadingValue(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            fetchWrapper.generic(endpointDetails(userId, countryId), requestOptions)
                .then(handleSuccess)
                .then(setValue)
                .catch(err => setErrorValue(handleError(err)))
                .finally(() => setLoadingValue(false))
        }

        return () => {
            controller.abort()
            setLoadingValue(false)
        }
    }, [loggedIn, countryId]);


    return {
        value,
        setValue,
        loadingValue,
        setLoadingValue,
        errorValue,
        setErrorValue,
        clearError: () => setErrorValue(null)
    }
}


export function useCountryCount(queryOption = null) {

    const {loggedIn, userId, token} = useAuth()
    const [searchParams] = useSearchParams()
    const [loadingCount, setLoadingCount] = useState(false);
    const [errorCount, setErrorCount] = useState(null);
    const [count, setCount] = useState(0);

    // Search options
    const query = queryOption || searchParams.get("query")

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn) {
            setErrorCount(null)
            setLoadingCount(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            // Search params
            const search = {query}

            fetchWrapper.generic(endpointCount(userId, search), requestOptions)
                .then(handleSuccess)
                .then(setCount)
                .catch(err => setErrorCount(handleError(err)))
                .finally(() => setLoadingCount(false))
        }

        return () => {
            controller.abort()
            setLoadingCount(false)
        }
    }, [loggedIn, query]);

    return {
        count,
        setCount,
        loadingCount,
        errorCount,
        clearErrorCount: () => setErrorCount(null)
    }
}

export function useCountryList(pageSizeOption = null, pageIndexOption = null, queryOption = null, orderByOption = null, keepPrev = false) {

    const defaultPageSize = 25
    const defaultPageIndex = 0

    const {loggedIn, userId, token} = useAuth()
    const [searchParams, setSearchParams] = useSearchParams()
    const [loadingList, setLoadingList] = useState(false);
    const [errorList, setErrorList] = useState(null);
    const [list, setList] = useState([]);
    const [reload, setReload] = useState(0);

    // Search options
    const pageSize = pageSizeOption || Number(searchParams.get("size")) || defaultPageSize
    const pageIndex = pageIndexOption || Number(searchParams.get("index")) || defaultPageIndex
    const query = queryOption || searchParams.get("query")
    const orderBy = orderByOption || searchParams.get("by")

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn && pageIndex != null && pageSize != null) {
            setErrorList(null)
            setLoadingList(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            // Search params
            const search = {
                offset: pageIndex * pageSize,
                limit: pageSize,
                query: query,
                order_by: orderBy
            }

            fetchWrapper.generic(endpointList(userId, search), requestOptions)
                .then(handleSuccess)
                .then(data => setList(prev => keepPrev ? ([...prev, ...data]) : data))
                .catch(err => setErrorList(handleError(err)))
                .finally(() => setLoadingList(false))
        }

        return () => {
            controller.abort()
            setLoadingList(false)
        }
    }, [loggedIn, pageIndex, pageSize, query, orderBy, reload]);

    return {
        list,
        setList: fields => {
            setList(prev => {
                const fieldsNew = (typeof fields === "function") ? fields(prev) : fields
                if (fieldsNew.length === 0 && pageIndex > 0) {
                    setSearchParams(prev => {
                        prev.set("index", `${pageIndex - 1}`)
                    })
                } else if (fieldsNew.length < prev.length) {
                    setReload(reload + 1)
                }
                return [...fieldsNew]
            })
        },
        loadingList,
        errorList,
        clearErrorList: () => setErrorList(null)
    }
}

export function useCountryOrderOptions(defaultOptions = []) {
    const {loggedIn, userId, token} = useAuth()
    const [loadingOrderOptions, setLoadingOrderOptions] = useState(false);
    const [errorOrderOptions, setErrorOrderOptions] = useState(null);
    const [orderOptions, setOrderOptions] = useState(defaultOptions);

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn) {
            setErrorOrderOptions(null)
            setLoadingOrderOptions(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            fetchWrapper.generic(endpointOrderOptions(userId), requestOptions)
                .then(handleSuccess)
                .then(setOrderOptions)
                .catch(err => setErrorOrderOptions(handleError(err)))
                .finally(() => setLoadingOrderOptions(false))
        }

        return () => {
            controller.abort()
            setLoadingOrderOptions(false)
        }
    }, [loggedIn]);


    return {
        orderOptions,
        setOrderOptions,
        loadingOrderOptions,
        setLoadingOrderOptions,
        errorOrderOptions,
        setErrorOrderOptions,
        clearErrorOrderOptions: () => setErrorOrderOptions(null)
    }
}